import { getNodeAttributes } from '@tiptap/core';
import { Editor } from '@tiptap/react';
import { ModifyFontSizeOperation } from 'widgets/TextBasedWidgets/common/extensions/fontSize';

import { MultiEditWidgetConfig } from '../../Widget.types';
import { TextWidgetData } from './TextWidget.types';

/**
 * Get the multi edit config for the text widget
 * To update and get/read text widget data the Editor is required
 *
 * @param editor - the editor instance
 */
const getMultiEditConfig = (editor: Editor | null) => {
  if (!editor) return {};

  const multiSelect: MultiEditWidgetConfig<TextWidgetData> = {
    fontSize: {
      getValue: () => {
        const textStyle = editor.getAttributes('textStyle');
        const fontSize = String(parseFloat(textStyle?.fontSize) ?? '');

        return fontSize;
      },
      onChange: ({ value: newFontSize }: { value: string }) => {
        editor.commands.focus('all');
        editor.chain().focus().setFontSize(`${newFontSize}px`).run();
        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };

        return updatedWidgetData;
      },
      onIncrement: () => {
        editor.commands.focus('all');
        editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Increment).run();
        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };
        return updatedWidgetData;
      },
      onDecrement: () => {
        editor.commands.focus('all');
        editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Decrement).run();
        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };
        return updatedWidgetData;
      },
    },
    fontFamily: {
      getValue: () => {
        const textStyle = editor.getAttributes('textStyle');
        return textStyle?.fontFamily;
      },
      onChange: ({ value: fontFamily }: { value: string }) => {
        editor.commands.focus('all');
        editor.chain().focus().setFontFamily(fontFamily).run();
        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };
        return updatedWidgetData;
      },
    },
    textColor: {
      onChange: ({ value: newColor }: { value: string }) => {
        editor.commands.focus('all');
        editor.chain().focus().setColor(newColor).run();
        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };

        return updatedWidgetData;
      },
      getValue: () => {
        const textStyle = editor.getAttributes('textStyle');
        return textStyle?.color;
      },
    },
    fontWeight: {
      onChange: ({ value: isBold }: { value: boolean }) => {
        editor.commands.focus('all');
        if (isBold) editor.chain().focus().setBold().run();
        else editor.chain().focus().unsetBold().run();

        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };

        return updatedWidgetData;
      },
      getValue: () => {
        return !!editor.isActive('bold');
      },
    },
    fontStyle: {
      onChange: ({ value: isItalic }: { value: boolean }) => {
        editor.commands.focus('all');
        if (isItalic) editor.chain().focus().setItalic().run();
        else editor.chain().focus().unsetItalic().run();

        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };

        return updatedWidgetData;
      },
      getValue: () => {
        return !!editor.isActive('italic');
      },
    },
    textAlign: {
      onChange: ({ value: alignment }: { value: string }) => {
        editor.commands.focus('all');
        editor.chain().focus().setTextAlign(alignment).run();

        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };

        return updatedWidgetData;
      },
      getValue: () => {
        if (!editor) return 'left';

        const nodeAttributes = getNodeAttributes(editor.state, editor.schema.nodes.paragraph);
        const textAlign = nodeAttributes?.textAlign;
        return textAlign;
      },
    },
    textDecoration: {
      onChange: ({ value: isUnderline }: { value: boolean }) => {
        editor.commands.focus('all');
        if (isUnderline) editor.chain().focus().setUnderline().run();
        else editor.chain().focus().unsetUnderline().run();

        const newProseMirrorData = editor.getJSON();
        const updatedWidgetData = { proseMirrorData: newProseMirrorData };

        return updatedWidgetData;
      },
      getValue: () => {
        return !!editor.isActive('underline');
      },
    },
  };

  return multiSelect;
};

export { getMultiEditConfig };
